import type { StartFlowIntent } from './types'

/**
 * AI 助手浮窗的**窗口 / 会话列表**状态机（F3 §2.1、§6 第 8 点）。纯函数，不碰 React。
 *
 * - 一个浮窗、多个对话（标签页）：同一入口 + 同一对象 = 同一个标签，再点入口切过去不新开；
 * - 同时最多 `MAX_SESSIONS` 个标签，满了拒绝**新开**（切到已有的照常）；
 * - 每条会话的消息 / 流不在这里（各自一个 `useChatStream`），这里只管有哪些、哪个在前台、窗口开没开。
 */
export const MAX_SESSIONS = 5

export interface AssistantSubject {
  type: string
  id: string
  /**
   * **只给前端显示**（上下文头 / 标签），**不发给后端**（entry_context.subject 只收 type + id）。
   * C0 09-14：必须是调用方页面上已经显示给用户的文字（如任务标题 / 业务编号），**不能用 id 拼**（界面不露 id）。
   */
  label?: string
}

export interface AssistantOpenRequest {
  /** 后端入口注册表里的 id（`global` / 以后的 `task_create` …） */
  entry: string
  subject?: AssistantSubject
  /**
   * = `entry_context.intent_hint`（后端注册表 id，`^[a-z][a-z0-9_]*$`，如 rewrite_description）。
   * C0 09-14 决策：**原样透传，前端不显示、不做任何分支**。后端目前不消费，仅透传。
   */
  hint?: string
  /**
   * 打开后**立刻起跑**的 Flow（页面专用入口，如「让助手重排这一天」，F2 计划 §10）。
   * 每次 open 都起跑一次、用这次给的参数；不进会话身份（sameTarget 不看它），不随刷新恢复。
   */
  startFlow?: StartFlowIntent
  /**
   * 新 Agent 平台（P3f）：打开后**立刻发出**的一句话（页面专用入口，如「让助手重排这一天」→ 运营助手）。
   * 同 startFlow：每次 open 都发一次，不进会话身份，不随刷新恢复。
   */
  startMessage?: string
  /**
   * 页面专用助手绑着的页面：路径前缀，如 `/settings/configuration`（该页和它下面的页都算）。
   * 离开这个页面后，这个标签不再停在前台：前台换成通用助手（没有就开一个），标签和对话都保留，回到页面再点入口就切回来。
   * 不给 = 不绑页面（通用助手、Agents 卡片的 Start chat、会话链接）。不进会话身份（sameTarget 不看它），随刷新恢复。
   */
  page?: string
}

/** 通用助手的入口 id（顶栏入口、「切回通用助手」都开它） */
export const GENERAL_ENTRY = 'global'

/** `pathname` 是不是 `page` 这个页面或它下面的页面（`/a/b` 不算 `/a/bc` 下面）。 */
export function isOnPage(pathname: string, page: string): boolean {
  const base = page.endsWith('/') ? page.slice(0, -1) : page
  return pathname === base || pathname.startsWith(`${base}/`)
}

export interface AssistantSession extends Omit<AssistantOpenRequest, 'startFlow' | 'startMessage'> {
  id: string
  /** 等会话发出的一次性起跑；nonce 每次 open 都换，同样的参数连点两次也是两次 */
  pendingStart?: { intent: StartFlowIntent; nonce: string }
  /** 同上，新 Agent 平台的一次性开场白 */
  pendingMessage?: { text: string; nonce: string }
}

/** 请求 → 会话：startFlow / startMessage 换成带 nonce 的 pendingStart / pendingMessage（没有就不带） */
const toSession = ({ startFlow, startMessage, ...rest }: AssistantOpenRequest, id: string, nonce: string): AssistantSession =>
  ({ ...rest, id, ...(startFlow ? { pendingStart: { intent: startFlow, nonce } } : {}),
    ...(startMessage ? { pendingMessage: { text: startMessage, nonce } } : {}) })

export type AssistantWindowState = 'closed' | 'open' | 'minimized'

export interface AssistantState {
  windowState: AssistantWindowState
  sessions: AssistantSession[]
  activeId: string | null
}

export type AssistantAction =
  | { type: 'open'; request: AssistantOpenRequest; newId: string }
  | { type: 'minimize' }
  | { type: 'restore' }
  | { type: 'closeWindow' }
  | { type: 'closeSession'; id: string }
  | { type: 'activate'; id: string }
  /** 刷新恢复（§8）：只从初始态（关着、没有会话）接管，已经有人打开过就不覆盖 */
  | { type: 'hydrate'; state: AssistantState }
  /** 路由变了（或刚恢复）：前台标签绑着别的页面时，把前台换成通用助手。`newId` 只在要新开通用助手时用 */
  | { type: 'syncPage'; pathname: string; newId: string }

export const initialAssistantState: AssistantState = { windowState: 'closed', sessions: [], activeId: null }

const sameTarget = (s: AssistantOpenRequest, r: AssistantOpenRequest) =>
  s.entry === r.entry && (s.subject?.type ?? null) === (r.subject?.type ?? null) && (s.subject?.id ?? null) === (r.subject?.id ?? null)

const findSession = (state: AssistantState, request: AssistantOpenRequest) => state.sessions.find(s => sameTarget(s, request))

/** `open` 会发生什么：切到已有标签 / 新开 / 满了拒绝。调用方据此给提示（reducer 本身拒绝时原样返回 state）。 */
export function openAssistantResult(state: AssistantState, request: AssistantOpenRequest): 'opened' | 'activated' | 'limit' {
  if (findSession(state, request)) return 'activated'
  return state.sessions.length >= MAX_SESSIONS ? 'limit' : 'opened'
}

export function assistantReducer(state: AssistantState, action: AssistantAction): AssistantState {
  switch (action.type) {
    case 'open': {
      const existing = findSession(state, action.request)
      // 同一目标再打开：切过去，label / hint 换成这次请求带的（页面标题可能改过；hint 以最近一次入口为准）
      if (existing) {
        const sessions = state.sessions.map(s => (s.id === existing.id ? toSession(action.request, existing.id, action.newId) : s))
        return { ...state, windowState: 'open', sessions, activeId: existing.id }
      }
      if (state.sessions.length >= MAX_SESSIONS) return state
      const session = toSession(action.request, action.newId, action.newId)
      return { windowState: 'open', sessions: [...state.sessions, session], activeId: session.id }
    }
    case 'minimize':
      return state.windowState === 'open' ? { ...state, windowState: 'minimized' } : state
    case 'restore':
      return state.windowState === 'minimized' ? { ...state, windowState: 'open' } : state
    case 'closeWindow':
      return initialAssistantState
    case 'hydrate':
      if (state.windowState !== 'closed' || state.sessions.length) return state
      return action.state.sessions.length ? action.state : state
    case 'syncPage': {
      if (state.windowState === 'closed') return state
      const active = state.sessions.find(s => s.id === state.activeId)
      if (!active?.page || isOnPage(action.pathname, active.page)) return state
      const general = state.sessions.find(s => s.entry === GENERAL_ENTRY)
      if (general) return { ...state, activeId: general.id }
      // 满了：先找一个不绑别的页面的标签，不为此挤出第 6 个
      const allowedHere = state.sessions.length >= MAX_SESSIONS
        && state.sessions.find(s => !s.page || isOnPage(action.pathname, s.page))
      if (allowedHere) return { ...state, activeId: allowedHere.id }
      // 上限管的是用户自己开的标签；这里是替用户换前台，全都绑着别的页面时也要开出通用助手
      const session = toSession({ entry: GENERAL_ENTRY }, action.newId, action.newId)
      return { ...state, sessions: [...state.sessions, session], activeId: session.id }
    }
    case 'activate':
      return state.sessions.some(s => s.id === action.id) ? { ...state, activeId: action.id } : state
    case 'closeSession': {
      const idx = state.sessions.findIndex(s => s.id === action.id)
      if (idx === -1) return state
      const sessions = state.sessions.filter(s => s.id !== action.id)
      if (!sessions.length) return initialAssistantState
      // 关的是前台标签：优先右边那个，没有就左边
      const activeId = state.activeId === action.id ? sessions[Math.min(idx, sessions.length - 1)].id : state.activeId
      return { ...state, sessions, activeId }
    }
  }
}
